/** Human-readable names for major index ETFs shown in prediction UI. */
export const INDEX_ETF_LABELS: Record<string, string> = {
  SPY: 'S&P 500',
  QQQ: 'Nasdaq-100',
  DIA: 'Dow Jones',
  IWM: 'Russell 2000',
};

/** GICS sector ETF tickers → sector names (mirrors backend market_session_predictor/sectors.py). */
export const SECTOR_ETF_LABELS: Record<string, string> = {
  XLP: 'Consumer Staples',
  XLU: 'Utilities',
  XLRE: 'Real Estate',
  XLV: 'Healthcare',
  XLF: 'Financials',
  XLI: 'Industrials',
  XLC: 'Communication Services',
  XLE: 'Energy',
  XLB: 'Materials',
  XLY: 'Consumer Discretionary',
  XLK: 'Technology',
};

const SORTED_SECTOR_TICKERS = Object.keys(SECTOR_ETF_LABELS).sort((a, b) => b.length - a.length);

export function formatSectorEtfLabel(ticker: string): string {
  const name = SECTOR_ETF_LABELS[ticker];
  return name ? `${name} (${ticker})` : ticker;
}

/** Label for index or sector leg chips in scenario cards. */
export function formatAssetLegLabel(ticker: string): string {
  if (SECTOR_ETF_LABELS[ticker]) return formatSectorEtfLabel(ticker);
  if (INDEX_ETF_LABELS[ticker]) return `${INDEX_ETF_LABELS[ticker]} (${ticker})`;
  return ticker;
}

/** Sector outlook chips may be full names or bare ETF tickers. */
export function formatSectorOutlookLabel(value: string): string {
  if (SECTOR_ETF_LABELS[value]) return formatSectorEtfLabel(value);
  return value;
}

/** Expand bare sector ETF tickers in prose — skips text already labeled "Name (XLK)". */
export function expandSectorTickersInText(text: string | null | undefined): string {
  if (!text?.trim()) return text?.trim() ?? '';
  let out = text;
  for (const sym of SORTED_SECTOR_TICKERS) {
    const name = SECTOR_ETF_LABELS[sym];
    const alreadyLabeled = new RegExp(`${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(${sym}\\)`, 'i');
    if (alreadyLabeled.test(out)) continue;
    out = out.replace(new RegExp(`\\b${sym}\\b`, 'g'), formatSectorEtfLabel(sym));
  }
  return out;
}

/** Turn engine keys like `tech_giveback` into readable labels. */
export function humanizePredictionLabel(value: string | null | undefined): string {
  if (!value?.trim()) return '';
  return value
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Detect machine-generated rationales that should not be shown as user copy. */
export function isInternalPredictionRationale(text: string | null | undefined): boolean {
  if (!text?.trim()) return false;
  const normalized = text.trim().toLowerCase();
  return (
    normalized.startsWith('rule overlay:') ||
    normalized.includes('oil_live=') ||
    normalized.includes('risks=')
  );
}

/** Detect key_input strings that are internal model audit trail (not for public display). */
export function isInternalKeyInput(text: string | null | undefined): boolean {
  if (!text?.trim()) return false;
  const t = text.trim();
  return (
    t.startsWith('ANALOG_') ||
    /^(ANALOG_INDEX_PRIOR|ANALOG_SECTOR_PRIOR|ANALOG_CROSS_ASSET_PRIOR)\b/.test(t) ||
    /\bregime_context\b/.test(t) ||
    /\bindex_divergence_/.test(t) ||
    /\b(mixed_absorption_likely|fragility_points)\b/.test(t) ||
    /\bdia_minus_spy_pct\b/.test(t) ||
    /\biwm_minus_spy_pct\b/.test(t) ||
    /\bqqq_minus_spy_pct\b/.test(t)
  );
}

/** Strip internal model artefacts from a prose string before public display. */
export function sanitizePredictionProse(text: string | null | undefined): string {
  if (!text?.trim()) return text?.trim() ?? '';
  return text
    .replace(/\bANALOG_(INDEX|SECTOR|CROSS_ASSET)_PRIOR\b[^;,.]*/g, '')
    .replace(/\banalog (prior|base rate)\b/gi, 'historical pattern')
    .replace(/\btrimmed.mean\b/gi, 'historical average')
    .replace(/\bsimilarity.weighted\b/gi, 'pattern-matched')
    .replace(/\b\d+\/\d+ positive analogs\b/gi, '')
    .replace(/\bregime_context\b/gi, 'market regime')
    .replace(/\bmixed_absorption_likely\b/gi, 'mixed follow-through')
    .replace(/\bNIM\b/g, 'net interest margins')
    .replace(/\bindex_divergence_\w+/g, 'index spread')
    .replace(/  +/g, ' ')
    .replace(/[;,]\s*$/, '')
    .trim();
}

function formatSignedPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export type MarketPredictionThesisInput = {
  analysisExcerpt?: string | null;
  marketViewThesis?: string | null;
  weeklyThesis?: string | null;
  baseCaseRationale?: string | null;
  surpriseRationale?: string | null;
  narrativeScenarioTilt?: string | null;
  rotationExpectation?: string | null;
  overallDirection?: string | null;
  predictedSpyPct?: number | null;
  catalystEvent?: string | null;
  catalystBucket?: string | null;
};

/** Build a readable thesis from structured prediction fields. */
export function buildMarketPredictionThesis(input: MarketPredictionThesisInput): string | null {
  const candidates = [
    input.marketViewThesis,
    input.weeklyThesis,
    input.baseCaseRationale,
    input.surpriseRationale,
    input.analysisExcerpt,
  ];
  for (const candidate of candidates) {
    if (candidate && !isInternalPredictionRationale(candidate)) {
      return expandSectorTickersInText(candidate.trim());
    }
  }

  const parts: string[] = [];

  if (input.overallDirection && input.predictedSpyPct != null) {
    parts.push(
      `Base case is a ${input.overallDirection} session with SPY ${formatSignedPct(input.predictedSpyPct)}.`,
    );
  }

  if (input.catalystEvent) {
    const bucket = input.catalystBucket
      ? ` (${humanizePredictionLabel(input.catalystBucket)} vs consensus)`
      : '';
    parts.push(`${input.catalystEvent} is the main catalyst${bucket}.`);
  }

  if (input.narrativeScenarioTilt || input.rotationExpectation) {
    const narrativeBits: string[] = [];
    if (input.narrativeScenarioTilt) {
      narrativeBits.push(humanizePredictionLabel(input.narrativeScenarioTilt));
    }
    if (input.rotationExpectation) {
      narrativeBits.push(`${humanizePredictionLabel(input.rotationExpectation)} rotation`);
    }
    parts.push(`Positioning overlay points to ${narrativeBits.join(' with ')}.`);
  }

  return parts.length > 0 ? expandSectorTickersInText(parts.join(' ')) : null;
}

export type MarketAnalysisThemeShape = {
  rank?: number;
  theme: string;
  significance?: string;
  direction?: string;
  mechanism?: string;
  market_impact?: string;
};

/** Composer files nest session data under `prediction`; legacy merge uses `next_session_prediction`. */
export function resolveNextSessionBlock(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!payload) return null;
  const legacy = payload.next_session_prediction;
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
    return legacy as Record<string, unknown>;
  }
  const composer = payload.prediction;
  if (composer && typeof composer === 'object' && !Array.isArray(composer)) {
    return composer as Record<string, unknown>;
  }
  return null;
}

export function resolveComposerScenarios(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!payload) return null;
  const nextSession = resolveNextSessionBlock(payload);
  const nested = nextSession?.scenarios;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  const topLevel = payload.scenarios;
  if (topLevel && typeof topLevel === 'object' && !Array.isArray(topLevel)) {
    return topLevel as Record<string, unknown>;
  }
  return null;
}

/** Map a plain key-driver string to a theme card without arbitrary mid-sentence truncation. */
export function keyDriverToThemeCard(text: string, index: number): MarketAnalysisThemeShape {
  const expanded = expandSectorTickersInText(text.trim());
  const sentenceEnd = expanded.search(/[.!?](?:\s|$)/);
  const firstSentence =
    sentenceEnd >= 0 ? expanded.slice(0, sentenceEnd + 1).trim() : expanded;
  const hasMore = expanded.length > firstSentence.length;

  return {
    rank: index + 1,
    theme: hasMore ? firstSentence : expanded,
    significance: index < 2 ? 'high' : 'medium',
    direction: 'mixed',
    mechanism: hasMore ? expanded : undefined,
    market_impact: '',
  };
}

/** Build theme cards from Composer `analysis.key_drivers` when forward_drivers are absent. */
export function forwardDriversFromComposerPayload(
  payload: Record<string, unknown> | null | undefined,
): MarketAnalysisThemeShape[] {
  if (!payload) return [];

  const nextSession = resolveNextSessionBlock(payload);
  const raw = nextSession?.forward_drivers;
  if (Array.isArray(raw) && raw.length > 0) {
    const drivers: MarketAnalysisThemeShape[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      const theme = typeof row.theme === 'string' ? row.theme.trim() : '';
      if (!theme) continue;
      const mechanism =
        typeof row.mechanism === 'string'
          ? expandSectorTickersInText(row.mechanism)
          : undefined;
      const rank = typeof row.rank === 'number' ? row.rank : drivers.length + 1;
      const normalizedTheme = expandSectorTickersInText(theme);
      const looksTruncated =
        mechanism != null &&
        mechanism.startsWith(normalizedTheme) &&
        mechanism.length > normalizedTheme.length;
      const card = looksTruncated
        ? keyDriverToThemeCard(mechanism, rank - 1)
        : {
            rank,
            theme: normalizedTheme,
            significance: typeof row.significance === 'string' ? row.significance : undefined,
            direction: typeof row.direction === 'string' ? row.direction : undefined,
            mechanism,
            market_impact:
              typeof row.market_impact === 'string'
                ? expandSectorTickersInText(row.market_impact)
                : undefined,
          };
      drivers.push(card);
    }
    return drivers;
  }

  const analysis = payload.analysis;
  if (!analysis || typeof analysis !== 'object' || Array.isArray(analysis)) return [];
  const keyDrivers = (analysis as Record<string, unknown>).key_drivers;
  if (!Array.isArray(keyDrivers)) return [];

  return keyDrivers
    .filter((text): text is string => typeof text === 'string' && text.trim().length > 0)
    .slice(0, 6)
    .map((text, index) => keyDriverToThemeCard(text, index));
}
